'use client'

/**
 * Headless PWA controller — registers the service worker and drives the build-version
 * "new version → Refresh" auto-update toast. NO install banner here anymore: the install
 * UI now lives in the explicit, always-visible components (no more stacked prompts):
 *   - components/pwa/get-app-section.tsx   (homepage + contact)
 *   - components/pwa/get-app-button.tsx    (admin header CTA)
 *   - components/pwa/install-app-reminder.tsx (admin EA reminder until installed)
 * all sharing components/pwa/use-install.ts (single beforeinstallprompt capture).
 */
import { useEffect } from 'react'
import { toast } from 'sonner'
import { trackEvent } from '@/lib/analytics'
import { isStandalone } from './pwa/use-install'
import { collectDeviceMetrics } from '@/lib/pwa-collect'

// ─── PWA auto-update (build-version check) ───────────────────────────────────
let updateToastShown = false

function promptRefresh() {
  if (updateToastShown) return
  updateToastShown = true
  toast('A new version of Spire is available', {
    description: 'Refresh to get the latest.',
    duration: Infinity,
    action: { label: 'Refresh', onClick: () => window.location.reload() },
  })
}

async function checkBuildVersion() {
  try {
    const res = await fetch('/api/version', { cache: 'no-store' })
    if (!res.ok) return
    const { buildId } = (await res.json()) as { buildId?: string }
    const current = process.env.NEXT_PUBLIC_BUILD_ID
    if (buildId && current && buildId !== current) promptRefresh()
  } catch {
    /* offline / transient — ignore */
  }
}

function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* SW optional */ })
  }
  void checkBuildVersion()
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkBuildVersion()
  })
  setInterval(() => void checkBuildVersion(), 30 * 60 * 1000)
}

export function PwaInstallPrompt() {
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      registerServiceWorker()
      if (isStandalone()) trackEvent('pwa_standalone_session') // installed-cohort signal
      collectDeviceMetrics() // Phase 3: one-per-session device/network/context snapshot
    }
  }, [])
  return null
}
